import {ManagingResources} from "../../../common/lifetime";
import {customElement, property} from "lit/decorators.js";
import {EventBus} from "../../../common/eventBus";
import {resolve} from "../../../container";
import {html, type PropertyValues, type TemplateResult} from "lit";
import {MOBILE_MENU_CLOSED, MOBILE_MENU_OPENED} from "./mobile/mobileHeaderEvents";
import {ScrollDirection, ScrollDirectionListener} from "../../../common/scroll";
import {GLOBAL} from "../../../common/globals";
import {Initializing, UnLitElement, WithEopSlots} from "../../../common/elements";
import {STICKY_HEADER_PRIORITY, StickyElements, StickyPriorityElement} from "../../elements/sticky/stickyElements";
import {Resolution} from "../../../common/resolution";
import {BOOTSTRAP} from "../../../common/resolutionConstants";
import {NavigationRootItem, NavModel} from "./common/navModel";
import {Page} from "../../../common/page";
import type {Nullable} from "../../../bootstrap/common/types";
import {HERO_TEASER_IMAGE_READY_EVENT} from "../../elements/heroTeaser/HeroTeaserImageReadyHandler";

export const HEADER_WITH_STABLE_OPACITY_EVENT = "header-has-stable-opacity";
export const HEADER_OPACITY_CHANGED_EVENT = "header-opacity-changed";

export type HeaderOpacityChangedEventData = { nowOpaque: boolean };

export function opacityChangedEventData(nowOpaque: boolean): HeaderOpacityChangedEventData {
    return {nowOpaque: nowOpaque};
}

const STATIC_HEADER_HEIGHT = 106;
const REDUCED_HEADER_HEIGHT = 72;
const MOBILE_HEADER_HEIGHT = 48;
const SCROLL_DOWN_OFFSET = STATIC_HEADER_HEIGHT - REDUCED_HEADER_HEIGHT;
const SCROLL_UP_OFFSET = 0;

@customElement("eop-header")
export class EopHeader extends WithEopSlots(Initializing(ManagingResources(UnLitElement))) {

    @property({attribute: "with-language-switch", type: Boolean})
    public withLanguageSwitch: boolean = false;
    @property({attribute: "with-dark-mode-toggle", type: Boolean})
    public withDarkModeToggle: boolean = false;
    @property({attribute: "transparent", type: Boolean})
    private transparentHeader: boolean = false;
    @property({type: Boolean, reflect: true})
    private reduced: boolean = false;
    @property({attribute: "opaque", type: Boolean, reflect: true})
    private currentlyOpaque: boolean = false;
    @property({type: Boolean})
    private sticky: boolean = false;
    @property({type: Boolean})
    private fullNavigation: boolean = false;
    @property({attribute: "brand-elements-slot-name"})
    public brandElementsSlotName: string;

    private navRoot: NavigationRootItem;

    public constructor(
        private page: Page = resolve(Page),
        private eventBus: EventBus = resolve(EventBus),
        private stickyElements: StickyElements = resolve(StickyElements),
        private resolution: Resolution = resolve(Resolution),
        private navModel: NavModel = resolve(NavModel)
    ) {
        super();
    }

    private stickyElement(): HTMLElement {
        return GLOBAL.bodyElement().querySelector("header")!;
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.resolution.onBootstrapBreakpointChange(() => this.requestUpdate(), this);
        this.navRoot = this.navModel.makeNavRoot();

        const scrollDirectionListener = ScrollDirectionListener.from(this, SCROLL_DOWN_OFFSET, SCROLL_UP_OFFSET);
        if (this.sticky) {
            this.stickyElements.register(new StickyPriorityElement(this.stickyElement())
                .withPriority(STICKY_HEADER_PRIORITY)
                .withName("header")
                .withStickyClass("sticky")
                .withElementHeightProvider(() => this.resolution.upTo(BOOTSTRAP.XS) ? MOBILE_HEADER_HEIGHT : REDUCED_HEADER_HEIGHT)
                .withEnableAction(() => {
                    if (this.fullNavigation) {
                        scrollDirectionListener.onScroll(this.scrollReductionHandler);
                    }

                    if (this.transparentHeader) {
                        this.currentlyOpaque = this.page.getYScrollPosition() > 0;
                        scrollDirectionListener.onScroll(this.headerDetachedHandler);
                    } else {
                        this.eventBus.dispatchEvent(HEADER_WITH_STABLE_OPACITY_EVENT);
                    }
                })
                .withDisableAction(() => {
                    scrollDirectionListener.offScroll(this.scrollReductionHandler);
                    scrollDirectionListener.offScroll(this.headerDetachedHandler);
                }));
        } else {
            this.eventBus.dispatchEvent(HEADER_WITH_STABLE_OPACITY_EVENT);
            this.page.registerViewportOffsetProvider(scrollTarget => Math.min(0, scrollTarget - this.clientHeight));
        }

        if (this.transparentHeader) {
            this.eventBus.on(HERO_TEASER_IMAGE_READY_EVENT, () => this.initialized());
            this.eventBus.on(MOBILE_MENU_OPENED, () => this.currentlyOpaque = true);
            this.eventBus.on(MOBILE_MENU_CLOSED, () => this.currentlyOpaque = (this.page.getYScrollPosition() > 0));
        }
    }

    private scrollReductionHandler = (direction: ScrollDirection): void => {
        this.reduced = direction === ScrollDirection.SCROLL_DOWN;
    };

    private headerDetachedHandler = (_: ScrollDirection, scrollY: number): void => {
        const opaqueBefore = this.currentlyOpaque;
        this.currentlyOpaque = scrollY > 0;
        if (this.currentlyOpaque !== opaqueBefore) {
            this.eventBus.dispatchEvent(HEADER_OPACITY_CHANGED_EVENT, opacityChangedEventData(this.currentlyOpaque));
        }
    };

    public disconnectedCallback(): void {
        this.stickyElements.unregister(this.stickyElement());
        super.disconnectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        if (!this.transparentHeader) {
            this.initialized();
        }
    }

    public render(): TemplateResult {
        return html`
            <div class="brand-area-spacer">
                <div class="brand-area-container">
                    ${this.resolveSlots(this.brandElementsSlotName)}
                </div>
            </div>
            ${this.renderHeaderMenu()}
        `;
    }

    private renderHeaderMenu(): Nullable<TemplateResult> {
        if (this.resolution.upTo(BOOTSTRAP.MD)) {
            return this.renderMobileHeaderMenu();
        } else {
            return this.renderDesktopHeaderMenu();
        }
    }

    private shouldRenderSearchLink(): boolean {
        return !!this.navModel.getSearchHref() || !!this.navRoot.getActiveSector()?.searchHref;
    }

    private renderMobileHeaderMenu(): Nullable<TemplateResult> {
        if (!this.shouldRenderSearchLink()
            && !this.navModel.hasMetaLinks()
            && this.navRoot.countSectors() <= 1
            && !(this.navRoot.getActiveSector()?.hasSubItems())
            && !this.withLanguageSwitch
        ) {
            return this.withDarkModeToggle
                ? html`
                        <eop-color-scheme-toggle></eop-color-scheme-toggle>`
                : null;
        }

        return html`
            <eop-mobile-header-menu
                    ?with-language-switch=${this.withLanguageSwitch}
                    ?with-dark-mode-toggle=${this.withDarkModeToggle}
                    ?sticky=${this.sticky}
            ></eop-mobile-header-menu>`;
    }

    private renderDesktopHeaderMenu(): TemplateResult {
        return html`
            <eop-desktop-header-menu
                    ?with-language-switch=${this.withLanguageSwitch}
                    ?transparent=${this.transparentHeader}
                    ?opaque=${this.currentlyOpaque}
                    ?reduced=${this.reduced}
                    ?full-navigation=${this.fullNavigation}
            ></eop-desktop-header-menu>`;
    }
}